import React, { useState, useEffect } from 'react';
import { schoolAdminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CREDIT_PRICE = 200; // kept for Paystack (200 NGN per credit) – unchanged
const MIN_MANUAL_AMOUNT = 10000;

const WalletPage = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState(1);
  const [loading, setLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [manualAmount, setManualAmount] = useState(MIN_MANUAL_AMOUNT);
  const [manualLoading, setManualLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);

  // USDT state
  const [showUSDTModal, setShowUSDTModal] = useState(false);
  const [usdtDetails, setUsdtDetails] = useState(null);

  useEffect(() => {
    fetchWallet();
    fetchTransactions();
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await schoolAdminAPI.getWallet();
      setWallet(res.data);
    } catch (error) {
      toast.error('Failed to load wallet');
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await schoolAdminAPI.getTransactions();
      setTransactions(res.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    }
  };

  // ===== Paystack (NGN) credit purchase =====
  const handleBuyCredits = async () => {
    setBuyLoading(true);
    try {
      const res = await schoolAdminAPI.initiatePayment({
        payment_type: 'credit_purchase',
        credits,
        callback_url: window.location.origin + '/admin/payment-callback',
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setBuyLoading(false);
    }
  };

  // ===== Bank Transfer manual top-up =====
  const handleManualRequest = async () => {
    if (manualAmount < MIN_MANUAL_AMOUNT) {
      toast.error(`Minimum amount is ₦${MIN_MANUAL_AMOUNT}`);
      return;
    }
    setManualLoading(true);
    try {
      const res = await schoolAdminAPI.requestManualTopup({ amount: manualAmount });
      setBankDetails(res.data);
      toast.success('Manual top-up request created. Use bank details below.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setManualLoading(false);
    }
  };

  // ===== USDT credit purchase =====
  const handleUSDTBuyCredits = async () => {
    if (credits < 1) {
      toast.error('Enter at least 1 credit');
      return;
    }
    const usdtEquiv = credits * 0.001;
    if (usdtEquiv < 50) {
      toast.error(`Minimum USDT top-up is 50 USDT (50,000 credits). You entered ${credits} credits (${usdtEquiv.toFixed(2)} USDT).`);
      return;
    }
    setLoading(true);
    try {
      const res = await schoolAdminAPI.initiateUSDT({
        payment_type: 'credit_purchase',
        credits: credits,
      });
      setUsdtDetails(res.data);
      setShowUSDTModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'USDT initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Wallet & Credits</h1>

      {/* Balance */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">Current Balance</p>
            <p className="text-4xl font-bold text-primary-600">{wallet?.credit_balance || 0} credits</p>
          </div>
        </div>
      </div>

      {/* Online Purchase (Paystack) */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Buy Credits Online (Paystack – NGN)</h2>
        <div className="flex flex-wrap items-end space-x-4">
          <div>
            <label className="block text-gray-700 mb-2">Number of Credits</label>
            <input
              type="number"
              min="1"
              value={credits}
              onChange={(e) => setCredits(parseInt(e.target.value) || 1)}
              className="border rounded-lg px-3 py-2 w-32"
            />
          </div>
          <div>
            <p className="text-gray-700 mb-2">Total: ₦{credits * CREDIT_PRICE}</p>
          </div>
          <button
            onClick={handleBuyCredits}
            disabled={buyLoading}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {buyLoading ? 'Processing...' : 'Buy Now'}
          </button>
        </div>
      </div>

      {/* USDT Purchase */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Buy Credits with USDT (ERC20)</h2>
        <p className="text-sm text-gray-600 mb-4">
          Rate: <strong>1 USDT = 1000 credits</strong> &nbsp;|&nbsp; Minimum: <strong>50 USDT</strong> (50,000 credits)
        </p>
        <div className="flex flex-wrap items-end space-x-4">
          <div>
            <label className="block text-gray-700 mb-2">Number of Credits</label>
            <input
              type="number"
              min="1"
              value={credits}
              onChange={(e) => setCredits(parseInt(e.target.value) || 1)}
              className="border rounded-lg px-3 py-2 w-32"
            />
          </div>
          <div>
            <p className="text-gray-700 mb-2">USDT: {(credits * 0.001).toFixed(2)} USDT</p>
          </div>
          <button
            onClick={handleUSDTBuyCredits}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Pay with USDT'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          You will receive a USDT wallet address. After sending, credits will be added upon admin verification.
        </p>
      </div>

      {/* Manual Bank Transfer */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Manual Bank Transfer</h2>
        <p className="text-gray-600 mb-4">Minimum amount: ₦{MIN_MANUAL_AMOUNT}</p>
        <div className="flex flex-wrap items-end space-x-4">
          <div>
            <label className="block text-gray-700 mb-2">Amount (₦)</label>
            <input
              type="number"
              min={MIN_MANUAL_AMOUNT}
              value={manualAmount}
              onChange={(e) => setManualAmount(parseInt(e.target.value) || MIN_MANUAL_AMOUNT)}
              className="border rounded-lg px-3 py-2 w-40"
            />
          </div>
          <button
            onClick={handleManualRequest}
            disabled={manualLoading}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {manualLoading ? 'Requesting...' : 'Request Bank Details'}
          </button>
        </div>

        {bankDetails && (
          <div className="mt-6 p-4 border border-green-200 bg-green-50 rounded-lg">
            <h3 className="font-semibold text-green-800 mb-2">Bank Transfer Details</h3>
            <p><strong>Bank:</strong> {bankDetails.bank_name}</p>
            <p><strong>Account Number:</strong> {bankDetails.account_number}</p>
            <p><strong>Account Name:</strong> {bankDetails.account_name}</p>
            <p><strong>Amount:</strong> ₦{bankDetails.amount}</p>
            <p><strong>Reference:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{bankDetails.reference}</code></p>
            <p className="text-sm text-gray-600 mt-2">Please use this reference when making the transfer. Your wallet will be credited after admin verification.</p>
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Recent Transactions</h2>
        {transactions.length === 0 ? (
          <p>No transactions yet.</p>
        ) : (
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Description</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id} className="border-b">
                  <td className="py-2">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="py-2">{t.description}</td>
                  <td className="py-2 capitalize">{t.transaction_type}</td>
                  <td className="py-2">{t.amount} credits</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== USDT Payment Modal ===== */}
      {showUSDTModal && usdtDetails && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <h3 className="text-lg font-bold mb-3 text-center">USDT Payment Details</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Amount to Send</p>
                <p className="font-bold text-lg text-blue-600">{usdtDetails.amount_usdt.toFixed(6)} USDT <span className="text-xs font-normal text-gray-500">(ERC20)</span></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Credits to Receive</p>
                <p className="font-medium">{usdtDetails.credits.toLocaleString()} credits</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Wallet Address</p>
                <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-lg mt-1">
                  <code className="text-xs break-all font-mono flex-1">{usdtDetails.wallet_address}</code>
                  <button onClick={() => copyToClipboard(usdtDetails.wallet_address)} className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reference</p>
                <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded block break-all">{usdtDetails.reference}</code>
              </div>
              <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 space-y-1">
                <p><strong>⚠️ Important:</strong> Send <strong>exactly {usdtDetails.amount_usdt.toFixed(6)} USDT</strong> on ERC20 network. After sending, master admin will verify.</p>
              </div>
              <div className="bg-blue-50 p-2 rounded text-xs text-blue-800">
                <p><strong>📞 Need help?</strong> Chat on WhatsApp: <a href="https://wa.me/2349039421996" target="_blank" rel="noopener" className="underline">+234 903 942 1996</a> or <a href="https://wa.me/2348113315252" target="_blank" rel="noopener" className="underline">+234 811 331 5252</a></p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => { setShowUSDTModal(false); setUsdtDetails(null); fetchWallet(); fetchTransactions(); }} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;